'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { 
  ChevronLeft, 
  Heart, 
  Share2, 
  Star, 
  Truck, 
  Shield, 
  MapPin,
  ChevronDown,
  ChevronUp,
  Clock,
  Users,
  ExternalLink
} from 'lucide-react';
import { Button } from '@myapp/ui/components/button';
import { Card, CardContent } from '@myapp/ui/components/card';
import { Badge } from '@myapp/ui/components/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@myapp/ui/components/accordion';

// 더미 상품 데이터
const product = {
  id: 1,
  slug: 'gangwon-potato-5kg',
  name: '강원도 평창 감자',
  price: 15000,
  originalPrice: 18000,
  unit: '5kg',
  discount: 17,
  rating: 4.8,
  reviewCount: 127,
  description: '평창의 청정 고원지대에서 자란 프리미엄 감자입니다. 포슬포슬한 식감과 달콤한 맛이 일품입니다.',
  images: [
    { id: 1, url: '/images/potato-1.jpg', alt: '강원도 감자 메인 이미지' },
    { id: 2, url: '/images/potato-2.jpg', alt: '강원도 감자 밭' },
    { id: 3, url: '/images/potato-3.jpg', alt: '수확한 감자들' },
    { id: 4, url: '/images/potato-4.jpg', alt: '감자 요리' },
    { id: 5, url: '/images/potato-5.jpg', alt: '감자 단면' },
  ],
  tags: ['이달의 BEST', '무농약', '산지직송', '당일배송'],
  origin: '강원도 평창군',
  harvest: '2024년 9월',
  storage: '서늘하고 통풍이 잘 되는 곳에 보관',
  shipping: '주문 후 1-2일 내 배송',
  farm: {
    name: '평창 행복농장',
    owner: '김농부',
    experience: '25년',
    certification: ['GAP인증', '친환경인증'],
    story: '3대째 이어온 감자 농사로 최고 품질의 감자를 재배하고 있습니다. 평창의 청정 자연환경과 풍부한 일교차가 만들어낸 달콤하고 포슬포슬한 감자를 자부심을 갖고 키우고 있습니다.',
    location: '강원도 평창군 대화면',
    contact: '033-123-4567'
  },
  nutrition: {
    calories: 77,
    carbs: 17.5,
    protein: 2.0,
    fat: 0.1,
    fiber: 2.2
  },
  features: [
    '포슬포슬한 식감',
    '자연 단맛',
    '영양가 풍부',
    '다양한 요리 활용'
  ]
};

const relatedRecipes = [
  { id: 1, title: '감자전', category: '전통요리', time: '30분', difficulty: '쉬움', image: '🥔', rating: 4.7 },
  { id: 2, title: '감자탕', category: '찌개/국', time: '90분', difficulty: '보통', image: '🍲', rating: 4.8 },
  { id: 3, title: '로스트 포테이토', category: '양식', time: '45분', difficulty: '쉬움', image: '🥔', rating: 4.6 },
  { id: 4, title: '감자샐러드', category: '반찬', time: '25분', difficulty: '쉬움', image: '🥗', rating: 4.5 },
  { id: 5, title: '감자수프', category: '스프', time: '40분', difficulty: '보통', image: '🍲', rating: 4.7 },
];

export default function ProductDetailPage({ params }: { params: { slug: string } }) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isFarmStoryOpen, setIsFarmStoryOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-16 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link href="/seasonal" className="flex items-center text-gray-600 hover:text-gray-900">
              <ChevronLeft className="w-5 h-5 mr-1" />
              <span>목록으로</span>
            </Link>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Heart className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 좌측: 이미지 갤러리 */}
          <div className="space-y-4">
            {/* 메인 이미지 */}
            <div className="aspect-square bg-gradient-to-br from-orange-100 to-yellow-100 rounded-lg overflow-hidden flex items-center justify-center text-8xl">
              🥔
            </div>

            {/* 썸네일 리스트 */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {product.images.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => setSelectedImageIndex(index)}
                  className={`flex-shrink-0 w-20 h-20 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-lg overflow-hidden flex items-center justify-center text-2xl border-2 transition-colors ${
                    selectedImageIndex === index ? 'border-green-500' : 'border-transparent hover:border-gray-300'
                  }`}
                >
                  🥔
                </button>
              ))}
            </div>
          </div>

          {/* 우측: 상품 정보 */}
          <div className="space-y-6">
            {/* 기본 정보 */}
            <div>
              <div className="flex flex-wrap gap-2 mb-3">
                {product.tags.map((tag, index) => (
                  <Badge key={index} variant={index === 0 ? "default" : "secondary"} className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
              
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
              <p className="text-gray-600 mb-4">{product.description}</p>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-500 fill-current" />
                  <span className="ml-1 font-semibold">{product.rating}</span>
                  <span className="text-gray-500 text-sm ml-1">({product.reviewCount})</span>
                </div>
                <div className="flex items-center text-gray-500">
                  <MapPin className="w-4 h-4 mr-1" />
                  <span className="text-sm">{product.origin}</span>
                </div>
              </div>

              {/* 가격 */}
              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-3xl font-bold text-green-600">₩{product.price.toLocaleString()}</span>
                <span className="text-sm text-gray-500">{product.unit}</span>
                {product.originalPrice && (
                  <>
                    <span className="text-lg text-gray-400 line-through">₩{product.originalPrice.toLocaleString()}</span>
                    <Badge variant="destructive" className="text-xs">{product.discount}% 할인</Badge>
                  </>
                )}
              </div>
            </div>

            {/* 상품 상세 정보 */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">원산지</span>
                  <span className="font-medium">{product.origin}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">수확시기</span>
                  <span className="font-medium">{product.harvest}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">보관방법</span>
                  <span className="font-medium text-right flex-1 ml-4">{product.storage}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">배송정보</span>
                  <span className="font-medium flex items-center">
                    <Truck className="w-4 h-4 mr-1 text-green-600" />
                    {product.shipping}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* 구매 버튼 (데스크톱) */}
            <div className="hidden lg:block space-y-3">
              <Button size="lg" className="w-full bg-green-600 hover:bg-green-700">
                <ExternalLink className="w-5 h-5 mr-2" />
                구매하러 가기
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1">
                  <Heart className="w-4 h-4 mr-2" />
                  찜하기
                </Button>
                <Button variant="outline" className="flex-1">
                  문의하기
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* 농가 스토리 */}
        <div className="mt-12">
          <Card>
            <CardContent className="p-6">
              <Accordion type="single" collapsible>
                <AccordionItem value="farm-story" className="border-none">
                  <AccordionTrigger className="text-lg font-semibold text-left hover:no-underline">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-green-600" />
                      <span>{product.farm.name} 농가 스토리</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">농장 정보</h4>
                          <div className="space-y-2 text-sm text-gray-600">
                            <div className="flex justify-between">
                              <span>농장주</span>
                              <span>{product.farm.owner}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>경력</span>
                              <span>{product.farm.experience}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>위치</span>
                              <span>{product.farm.location}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>연락처</span>
                              <span>{product.farm.contact}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">인증현황</h4>
                          <div className="flex flex-wrap gap-2">
                            {product.farm.certification.map((cert, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                <Shield className="w-3 h-3 mr-1" />
                                {cert}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">농가 이야기</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">{product.farm.story}</p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </div>

        {/* 관련 레시피 */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">이 재료로 만들 수 있는 레시피</h2>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {relatedRecipes.map((recipe) => (
              <Card key={recipe.id} className="flex-shrink-0 w-64 cursor-pointer hover:shadow-lg transition-shadow">
                <CardContent className="p-0">
                  <div className="aspect-video bg-gradient-to-br from-orange-100 to-yellow-100 flex items-center justify-center text-6xl">
                    {recipe.image}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">{recipe.category}</Badge>
                      <Badge variant="outline" className="text-xs">{recipe.difficulty}</Badge>
                    </div>
                    <h3 className="font-semibold mb-2">{recipe.title}</h3>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {recipe.time}
                      </div>
                      <div className="flex items-center">
                        <Star className="w-4 h-4 mr-1 text-yellow-500 fill-current" />
                        {recipe.rating}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* 모바일 하단 고정 CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 pb-safe z-40">
        <div className="flex gap-3">
          <Button variant="outline" size="lg" className="flex-shrink-0">
            <Heart className="w-5 h-5" />
          </Button>
          <Button size="lg" className="flex-1 bg-green-600 hover:bg-green-700">
            <ExternalLink className="w-5 h-5 mr-2" />
            구매하러 가기
          </Button>
        </div>
      </div>

      {/* 모바일 하단 여백 */}
      <div className="lg:hidden h-24" />
    </div>
  );
}